import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import IP_URL from '../connect';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../Navbar';
import Footer from '../Footer';
import Sidebar from '../Sidebar';
import { useSelector } from 'react-redux';
import { selectUser } from '../../Redux/currentUserSlice';

// ---------- Styled Components ----------
const Main = styled.div`
  background: #f4f6f8;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.2rem;
  }

  @media (max-width: 480px) {
    padding: 0.8rem;
  }
`;

const Section = styled(motion.div)`
  background: #ffffff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  padding: 2.5rem;
  max-width: 600px;
  width: 100%;
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 480px) {
    padding: 1.2rem;
    border-radius: 10px;
  }
`;

const Title = styled.h2`
  font-size: 1.8rem;
  color: #2d3436;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1.8rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.95rem;
  color: #333;
  font-weight: 500;
  margin-top: 1rem;

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.85rem;
  margin-top: 0.4rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.3s;

  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 4px rgba(0, 123, 255, 0.2);
  }

  @media (max-width: 480px) {
    padding: 0.7rem;
    font-size: 0.9rem;
    width:92%;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.85rem;
  margin-top: 0.4rem;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: white;
  font-size: 1rem;
  outline: none;

  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 4px rgba(0, 123, 255, 0.2);
  }

  @media (max-width: 480px) {
    padding: 0.7rem;
    font-size: 0.9rem;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.9rem;
  margin-top: 1.8rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #0056b3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding: 0.8rem;
    font-size: 0.95rem;
  }
`;

const Spinner = styled.div`
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid #fff;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  animation: spin 0.8s linear infinite;
  display: inline-block;
  margin-right: 8px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ---------- Component ----------
const AdminRegisterUrl = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const { token } = useSelector(selectUser);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!consumerKey || !consumerSecret || !shortCode || !selectedOrg) {
      toast.warning('⚠️ Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${IP_URL}/registerUrl/register-url`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          consumerKey,
          consumerSecret,
          shortCode,
          uniqueKey: selectedOrg,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('✅ M-Pesa URL registered successfully!');
        setConsumerKey('');
        setConsumerSecret('');
        setShortCode('');
        setSelectedOrg('');
      } else {
        toast.error(`❌ Error: ${data.error || 'Registration failed'}`);
      }
    } catch (error) {
      console.error('Register URL error:', error);
      toast.error('🚫 Network error or server unreachable');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all organizations
  useEffect(() => {
    const fetchAllOrgs = async () => {
      try {
        const response = await fetch(`${IP_URL}/organization/all-orgs`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();

        if (response.ok) {
          setOrganizations(data);
        } else {
          toast.error('Error fetching organizations');
        }
      } catch (error) {
        console.error('Error fetching orgs:', error);
        toast.error('⚠️ Unable to fetch organizations');
      }
    };

    if (token) fetchAllOrgs();
  }, [token]);

  return (
    <Main>
      <Navbar showMenuButton={true} toggleSidebar={toggleSidebar} />
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Container>
        <Section
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Title>Register M-Pesa C2B URL</Title>

          <form onSubmit={handleSubmit}>
            <Label>Consumer Key</Label>
            <Input
              type="text"
              value={consumerKey}
              onChange={(e) => setConsumerKey(e.target.value)}
              placeholder="Enter Consumer Key"
            />

            <Label>Consumer Secret</Label>
            <Input
              type="text"
              value={consumerSecret}
              onChange={(e) => setConsumerSecret(e.target.value)}
              placeholder="Enter Consumer Secret"
            />

            <Label>Short Code</Label>
            <Input
              type="text"
              value={shortCode}
              onChange={(e) => setShortCode(e.target.value)}
              placeholder="e.g. 4184223"
            />

            <Label>Select Organization</Label>
            <Select
              value={selectedOrg}
              onChange={(e) => setSelectedOrg(e.target.value)}
            >
              <option value="">-- Select Organization --</option>
              {organizations.map((org) => (
                <option key={org._id} value={org.uniqueKey}>
                  {org.userName}
                </option>
              ))}
            </Select>

            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner />
                  Registering...
                </>
              ) : (
                'Register URL'
              )}
            </Button>
          </form>
        </Section>
      </Container>

      <Footer />
      <ToastContainer position="bottom-right" theme="colored" />
    </Main>
  );
};

export default AdminRegisterUrl;
